type TimeFormat = Readonly<'default' | 'short' | 'absolute'>
/**
 * @see https://www.notion.so/drtail/b9018d2f8c484837864f3b9888eff05c
 * @param date ex) "2023-11-01T04:18:00.695597Z"
 * @param format 'default' | 'short | 'absolute'
 * @returns ex) '1 day ago' | '1d' | 'Wed, Nov 01, 2023'
 */
export const getDateFormat = (date: string, format: TimeFormat = 'default') => {
  const dateObject = new Date(date)
  const now = new Date()

  if (!date.length || (format !== 'absolute' && dateObject > now)) {
    return '' // date가 없거나, 현재 날짜보다 미래의 날짜가 삽입되었을 때에 대한 방어코드
  }

  const diffInMilliseconds = now.getTime() - dateObject.getTime()
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60))
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24))

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'always' })

  const timeFormats = {
    absolute: new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(dateObject),
    default: {
      now: 'Now',
      minute: rtf.format(-diffInMinutes, 'minute'),
      hour: rtf.format(-diffInHours, 'hour'),
      day: rtf.format(-diffInDays, 'day'),
      week: dateObject.toLocaleDateString('en', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
    },
    short: {
      now: 'Now',
      minute: `${diffInMinutes}m`,
      hour: `${diffInHours}h`,
      day: `${diffInDays}d`,
      week: `${Math.floor(diffInDays / 7)}w`,
    },
  }

  if (format === 'absolute') {
    return timeFormats[format]
  }

  switch (true) {
    case diffInMinutes < 1: // 1분 미만
      return timeFormats[format].now
    case diffInMinutes < 60: // 1분 ~ 59분
      return timeFormats[format].minute
    case diffInHours < 24: // 1시간 ~ 23시간
      return timeFormats[format].hour
    case diffInDays >= 1 && diffInDays < 7: // 1일 ~ 6일
      return timeFormats[format].day
    default: // 7일 ~
      return timeFormats[format].week
  }
}

/**
 *
 * @param dateStr ex) "2022-10-13T00:00:00"
 * @returns ex) "Oct 13, 2022"
 */
export const convertIsoToReadable = (dateStr: string) => {
  // ISO 형식의 날짜 문자열을 Date 객체로 변환
  const dateObj = new Date(dateStr)

  // 날짜 형식을 설정
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }

  // 날짜를 원하는 형식의 문자열로 변환
  const readableDateStr = new Intl.DateTimeFormat('en-US', options).format(dateObj)

  return readableDateStr
}

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const parseDate = (ms: number | null): string => {
  if (!ms) {
    return ''
  }
  const d = new Date(ms)
  return `${months[d.getMonth()]}. ${d.getDate()}. ${d.getFullYear()}`
}

export const isMoreThan24Hours = (dateTime: Date | string) => {
  const now = Date.now()
  const timeDifference = now - new Date(dateTime).getTime()

  const twentyFourHoursInMilliseconds = 24 * 60 * 60000
  return timeDifference > twentyFourHoursInMilliseconds
}
